/**
 * SettingsPage - edit your own details
 * ------------------------------------
 * Three separate forms, because they do three different things and each
 * should be saveable on its own:
 *   1. Profile details (and photo, for students)
 *   2. Password
 *   3. Membership status (posters only)
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Save, KeyRound, CreditCard, Trash2, AlertTriangle } from 'lucide-react';

import { authApi, safetyApi } from '../services/api';
import { shrinkImage } from '../services/image';
import { useAuth } from '../hooks/useAuth';
import { useI18n, LANGUAGES } from '../hooks/useI18n';
import { dateOnly, birthDate } from '../utils/format';
import BirthDateFields from '../components/BirthDateFields';
import LocationFields from '../components/LocationFields';
import Avatar from '../components/Avatar';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import BackLink from '../components/BackLink';
import NotificationSettings from '../components/NotificationSettings';
import NotificationPreferences from '../components/NotificationPreferences';
import InstallGuide from '../components/InstallGuide';
import TwoFactorSettings from '../components/TwoFactorSettings';
import PasswordInput from '../components/PasswordInput';

const CATEGORIES = [
  'Cleaning',
  'Gardening',
  'Babysitting',
  'Pet Care',
  'Tutoring',
  'Shopping & Errands',
  'Moving & Lifting',
  'Tech Help',
  'DIY & Repairs',
  'Other',
];

export default function SettingsPage() {
  const { user, setUser, isStudent, subscriptionsRequired, logout } = useAuth();
  const { t, lang, setLang } = useI18n();

  // Two halves, and both matter. setLang switches the SITE now; the save puts
  // it on the account, which is what every future email reads. See the card
  // below. A failed save is deliberately not shouted about - the language did
  // change, which is what they asked for, and the emails will catch up next
  // time anything is saved.
  /**
   * WHICH language was saved - 'it' or 'en' - and NOT the finished
   * sentence. That distinction is the whole bug this once had.
   *
   * It used to store the translated sentence, built at the moment of the
   * click. Two things then went wrong at once. `t` is rebuilt when the
   * language changes, so the one this function had closed over was still
   * the OLD language - and the sentence it produced was frozen into state
   * as plain text, so re-rendering in the new language could not correct
   * it either. Switching to English announced it in Italian, and switching
   * to Italian announced it in English.
   *
   * Keeping the code and translating it at render time fixes both: the
   * sentence is built fresh in whatever language the site is now in, and
   * it follows any further switch as well.
   *
   * The general rule, worth carrying elsewhere: **store what happened, not
   * the words for it.** Anything translated that is put into state stops
   * being translatable.
   */
  const [languageSaved, setLanguageSaved] = useState('');
  const changeLanguage = async (code) => {
    setLang(code);
    try {
      await authApi.updateProfile({ language: code });
      setLanguageSaved(code);
    } catch {
      setLanguageSaved('');
    }
  };
  const navigate = useNavigate();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteState, setDeleteState] = useState({ busy: false, error: '' });
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    safetyApi
      .blocks()
      .then((d) => setBlocks(d.blocks))
      .catch(() => setBlocks([]));
  }, []);

  const unblock = async (id) => {
    try {
      await safetyApi.unblock(id);
      setBlocks((current) => current.filter((b) => b.blocked?._id !== id));
    } catch {
      /* leave the list as it is */
    }
  };

  const [profile, setProfile] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    school: user.school || '',
    bio: user.bio || '',
    schoolYear: user.schoolYear || 'S5',
    maxTravelKm: user.maxTravelKm ?? '', // '' means "I do not mind"
  });
  const [skills, setSkills] = useState(user.skills || []);
  // Only for an account from before 17 September 2026 that has no date of
  // birth yet. Everybody else's is fixed and shown, not edited.
  const [addedBirthDate, setAddedBirthDate] = useState('');
  const [location, setLocation] = useState({
    city: user.location?.city || '',
    address: user.location?.address || '',
    lat: user.location?.lat ?? null,
    lng: user.location?.lng ?? null,
  });

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });

  const [profileState, setProfileState] = useState({ busy: false, error: '', notice: '' });
  const [passwordState, setPasswordState] = useState({ busy: false, error: '', notice: '' });
  const [photoState, setPhotoState] = useState({ busy: false, error: '', notice: '' });

  /**
   * DETECTING THE PICKER THAT NEVER CAME BACK.
   *
   * On an iPad this failed with no message at all, which should have been
   * impossible - every path in uploadPhoto writes something. It was
   * impossible: uploadPhoto was never running.
   *
   * iOS suspends a page while the photo picker is open and, under memory
   * pressure, DISCARDS IT ENTIRELY. Coming back reloads the site from
   * scratch. React remounts, the file input is new and empty, the selection
   * is gone, and no change event is ever delivered. From the person's side:
   * they picked a photo, pressed OK, landed back on their profile, and
   * nothing happened. Nothing is exactly what happened.
   *
   * We cannot stop iOS doing that. We can notice it, which is the difference
   * between a broken site and a site that says what went wrong. A flag goes
   * into sessionStorage before the picker opens and is cleared the moment
   * uploadPhoto runs. If it is still there when this page mounts, the page
   * died in between.
   *
   * sessionStorage and not a ref, because a ref does not survive the reload -
   * surviving the reload is the entire point.
   */
  const PICKER_FLAG = 'choreconnect_photo_picker_open';

  useEffect(() => {
    let stamp = null;
    try {
      stamp = sessionStorage.getItem(PICKER_FLAG);
      if (stamp) sessionStorage.removeItem(PICKER_FLAG);
    } catch {
      // Private browsing can refuse sessionStorage. Nothing here is important
      // enough to break the page over.
    }
    if (!stamp) return;

    // Ten minutes. Long enough to cover somebody browsing their photo library,
    // short enough that a flag left over from yesterday does not produce a
    // confusing message today.
    if (Date.now() - Number(stamp) > 10 * 60 * 1000) return;

    setPhotoState({
      busy: false,
      notice: '',
      error:
        'Your device closed ChoreConnect while the photo picker was open, so the photo never reached us. This happens on iPads when memory is short. Try again - and if it keeps happening, close some other tabs and apps first, or add ChoreConnect to your home screen and open it from there, which makes it much less likely.',
    });
  }, []);

  const handleProfileChange = (event) => {
    setProfile({ ...profile, [event.target.name]: event.target.value });
  };

  const toggleSkill = (skill) => {
    setSkills((current) =>
      current.includes(skill) ? current.filter((s) => s !== skill) : [...current, skill]
    );
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setProfileState({ busy: true, error: '', notice: '' });

    try {
      const payload = { ...profile, location };
      if (isStudent) {
        payload.skills = skills;
        // The age is no longer sent: it comes from the date of birth, on the
        // server. A date is sent only by an older account adding its own.
        if (!user.dateOfBirth && addedBirthDate) payload.dateOfBirth = addedBirthDate;
        // '' goes through as null, meaning no limit.
        payload.maxTravelKm = profile.maxTravelKm === '' ? null : Number(profile.maxTravelKm);
      }

      const { user: updated } = await authApi.updateProfile(payload);
      setUser(updated);
      setProfileState({ busy: false, error: '', notice: t('settingsPage.detailsSaved') });
    } catch (err) {
      setProfileState({ busy: false, error: err.message, notice: '' });
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    setPasswordState({ busy: true, error: '', notice: '' });

    try {
      await authApi.changePassword(passwords);
      setPasswords({ currentPassword: '', newPassword: '' });
      setPasswordState({ busy: false, error: '', notice: t('settingsPage.passwordUpdated') });
    } catch (err) {
      setPasswordState({ busy: false, error: err.message, notice: '' });
    }
  };

  /**
   * Choosing a photo.
   *
   * THIS FUNCTION IS NOT ALLOWED TO FAIL SILENTLY, and that is the whole
   * lesson of it. It used to open with `if (!file) return;`, so when an iPad
   * came back from the picker with nothing, the page simply sat there - no
   * message, no spinner, no change. From the outside that is indistinguishable
   * from a broken button, and it is impossible to report usefully: "I pressed
   * it and nothing happened" is all anybody can say.
   *
   * Every path below now ends in something written on the screen.
   *
   * THE ZERO-BYTE CASE IS REAL. iOS keeps photos in iCloud and leaves a
   * placeholder on the device. Tapping one in the picker puts an circled i on the
   * picture instead of opening it, and handing it to a website produces an
   * empty file or nothing at all, because the real image is still on Apple's
   * servers. Nothing we do to the upload can fix that - but
   * telling somebody "open it in Photos first so it downloads" turns an
   * unexplained failure into a thing they can do.
   */
  const uploadPhoto = async (event) => {
    const input = event.target;
    const file = input.files?.[0];

    // We got here, so the page survived the picker. Clear the breadcrumb
    // before anything else, or a later mount would report a failure that did
    // not happen.
    try {
      sessionStorage.removeItem(PICKER_FLAG);
    } catch {
      /* private browsing */
    }

    if (!file) {
      setPhotoState({
        busy: false,
        notice: '',
        error:
          'No photo came back from the picker. If you chose one from iCloud, open it in the Photos app first so it downloads to the device, then try again.',
      });
      return;
    }

    if (!file.size) {
      setPhotoState({
        busy: false,
        notice: '',
        error: `"${file.name || 'That photo'}" came through empty. It is probably stored in iCloud rather than on this device - open it in the Photos app first so it downloads, then try again.`,
      });
      return;
    }

    setPhotoState({ busy: true, error: '', notice: '' });
    try {
      // Prove the file can actually be READ before doing anything else. A
      // picker can hand over a file whose bytes are not available - iCloud
      // again, or a permission that was withdrawn - and the failure otherwise
      // surfaces somewhere much less obvious, as a broken shrink or a
      // zero-length upload the server rejects for the wrong reason.
      const probe = await file.slice(0, 1).arrayBuffer();
      if (!probe || probe.byteLength === 0) {
        throw new Error(
          'This device would not let us read that photo. If it is stored in iCloud, open it in the Photos app first so it downloads.'
        );
      }

      // Shrink a phone photo before sending it - see services/image.js.
      const ready = await shrinkImage(file);
      const { user: updated } = await authApi.uploadPhoto(ready);
      setUser(updated);
      setPhotoState({ busy: false, error: '', notice: t('settingsPage.photoUpdated') });
    } catch (err) {
      setPhotoState({
        busy: false,
        notice: '',
        error: err.message || t('settingsPage.photoFailed'),
        // What was actually picked, shown in small print under the error.
        // "I pressed it and nothing happened" is impossible to act on; "it
        // says IMG_0421.HEIC, 0 KB" is a diagnosis. Only ever rendered when
        // something has already gone wrong.
        detail: `${file.name || 'unnamed'} · ${file.type || 'unknown type'} · ${Math.round(file.size / 1024)} KB`,
      });
    } finally {
      // Clearing the value is what makes re-choosing the SAME file fire a
      // change event at all - without it the value has not changed, nothing
      // happens, and it looks like the site ignoring you.
      //
      // NOTE: the input is deliberately NOT remounted here any more. It used
      // to carry a key that changed after every attempt, on the theory that a
      // fresh element could not hold stale state. That was a guess at a bug
      // that was never reproduced, and it introduced a worse one: React can
      // replace the element while the picker is still open, and a selection
      // then lands on a node that is no longer in the document. The input
      // stays put; only its value is cleared.
      input.value = '';
    }
  };

  const removePhoto = async () => {
    setPhotoState({ busy: true, error: '', notice: '' });
    try {
      const { user: updated } = await authApi.removePhoto();
      setUser(updated);
      setPhotoState({ busy: false, error: '', notice: t('settingsPage.photoRemoved') });
    } catch (err) {
      setPhotoState({ busy: false, error: err.message, notice: '' });
    }
  };

  const deleteAccount = async () => {
    setDeleteState({ busy: true, error: '' });
    try {
      await authApi.deleteAccount(deletePassword);
      logout();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteState({ busy: false, error: err.message });
    }
  };

  return (
    <div className="container section" style={{ maxWidth: 760 }}>
      <BackLink to="/dashboard" label={t('settingsPage.backToDashboard')} />

      <div className="page-header">
        <h1>{t('settingsPage.title')}</h1>
        <p>
          {t('settingsPage.signedInAs', { email: user.email })} ·{' '}
          <Link to={`/${isStudent ? 'students' : 'users'}/${user._id}`}>{t('settingsPage.viewProfile')}</Link>
        </p>
      </div>

      {/* ---------------- LANGUAGE ----------------
          Near the top on purpose: somebody who has landed here because the
          site is in the wrong language should not have to read past six
          cards in that language to find the switch.

          It does two things at once, and the card says so. Changing it
          switches the site immediately, the same as the navbar switch - and
          it also saves to the account, which is what decides the language of
          every EMAIL. The server writes those with no browser to ask, so
          without the save half, somebody could read the site in Italian for a
          year and still get their password reset in English. */}
      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-xs)' }}>{t('settings.languageTitle')}</h3>
        <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
          {t('settings.languageBody')}
        </p>

        <div className="weekday-picker">
          {LANGUAGES.map((option) => (
            <button
              key={option.code}
              type="button"
              className={`weekday-btn${lang === option.code ? ' is-chosen' : ''}`}
              aria-pressed={lang === option.code}
              onClick={() => changeLanguage(option.code)}
            >
              {option.name}
            </button>
          ))}
        </div>
        {languageSaved && (
          <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
            {/* Both halves are looked up now, so the sentence AND the name
                of the language inside it are in the language being read. */}
            {t('settings.languageSaved', { language: t(`language.${languageSaved}`) })}
          </p>
        )}
      </div>

      {/* ---------------- PROFILE ---------------- */}
      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('settingsPage.yourDetails')}</h3>

        {/* Both roles since 17 August 2026. This used to be inside
            `isStudent &&`, so a poster was a name and a town to somebody
            deciding whether to go to their house. */}
        {/* The buttons sit in a `.row`, which centres its children - so the
            "Add a photo" button lines up with the middle of the avatar
            circle. Anything with its own height, like the notice and the
            error, goes BELOW the row rather than inside it: put them beside
            the avatar and the column grows taller than the circle, which
            drags the button off centre the moment either one appears. */}
        <div className="row" style={{ gap: 'var(--space-sm)' }}>
          <Avatar person={user} size="lg" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
              {/**
                * THE INPUT IS THE BUTTON. It is not hidden and triggered by a
                * label any more.
                *
                * It used to be `.sr-only` - one pixel, clipped, absolutely
                * positioned - with a <label for> in front of it. That is the
                * usual way to style a file input and it worked everywhere we
                * could test. On an iPad it opened the picker and then never
                * delivered the file: the change event simply never arrived,
                * three fixes in a row missed it, and the page sat there
                * saying nothing because no code of ours ever ran.
                *
                * So there is no proxy any more. The input covers the button
                * exactly, at full size with opacity 0, and the tap lands on
                * the input itself. Nothing has to forward anything.
                *
                * Do not "tidy this up" back into a hidden input with a label.
                * It looks neater and it is how this broke.
                */}
              <span
                className="btn btn-outline btn-sm btn-inline"
                style={{ position: 'relative', cursor: 'pointer' }}
              >
                <Upload size={16} aria-hidden="true" />
                {photoState.busy
                  ? t('settingsPage.working')
                  : user.profilePicture
                    ? t('settingsPage.changePhoto')
                    : t('settingsPage.addAPhoto')}
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/*"
                  aria-label={user.profilePicture ? t('settingsPage.changePhoto') : t('settingsPage.addPhoto')}
                  onClick={(e) => {
                    e.currentTarget.value = '';
                    try {
                      sessionStorage.setItem(PICKER_FLAG, String(Date.now()));
                    } catch {
                      /* private browsing - not worth failing over */
                    }
                  }}
                  onChange={uploadPhoto}
                  disabled={photoState.busy}
                  style={{
                    position: 'absolute',
                    // -2px, not 0, so the button's own border is covered too.
                    // An absolutely positioned child is laid out against its
                    // parent's PADDING box, which excludes the border - at
                    // inset 0 the input came out 137x37 inside a 141x41
                    // button, leaving a 2px rim that was not the input. On a
                    // touchscreen that rim is exactly where a thumb lands.
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    opacity: 0,
                    cursor: 'pointer',
                    // 16px stops iOS zooming the page when it is tapped, the
                    // same reason every other input on this site is 16px.
                    fontSize: 16,
                  }}
                />
              </span>

              {user.profilePicture && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-inline"
                  onClick={removePhoto}
                  disabled={photoState.busy}
                >
                  <Trash2 size={16} />
                  {t('settingsPage.removePhoto')}
                </button>
              )}
            </div>

          </div>
        </div>

        {/**
          * THE PLAIN WAY, for when the styled one will not work.
          *
          * This is a bare file input with no styling, no label, no wrapper and
          * NO `accept` attribute, and every one of those absences is the
          * point. It is the most compatible thing the web has.
          *
          * Dropping `accept` matters most on an iPad. With `accept="image/*"`
          * iOS goes straight to the Photos picker, which is where an iCloud
          * photo cannot be handed over at all. Without it, iOS offers
          * "Browse" as well, so a photograph already saved into Files can be
          * chosen without the Photos picker being involved.
          *
          * Offered permanently rather than only after a failure: somebody
          * whose first attempt did nothing has already learned not to trust
          * the button, and hiding the alternative behind another failure is
          * not a kindness.
          */}
        <details style={{ marginTop: 'var(--space-xs)' }}>
          <summary className="text-sm text-muted" style={{ cursor: 'pointer' }}>
            {t('settingsPage.photoNotUploading')}
          </summary>
          <div style={{ marginTop: 'var(--space-xs)' }}>
            {/**
              * THE FIRST ANSWER IS THE ONE THAT TURNED OUT TO BE TRUE.
              *
              * This took four attempts to find, and the cause was not in our
              * code at all: the photographs were in iCloud and not on the
              * device. iOS shows those in the picker, lets you choose one,
              * and then hands the website nothing - no file, and no event, so
              * nothing on our side ever runs and the page cannot even say it
              * failed. The tell is an circled i appearing ON THE PICTURE when it is
              * tapped, rather than the photo opening.
              *
              * Downloading the photo in the Photos app first fixes it
              * completely. That is written at the top, in the person's own
              * terms, because it is the answer nine times in ten - and
              * because somebody reading this has already had the button do
              * nothing to them at least once.
              */}
            <p className="text-sm">
              <strong>{t('settingsPage.photoInICloudQ')}</strong>
            </p>
            <p className="text-sm text-muted" style={{ marginTop: 4 }}>
              {t('settingsPage.photoSignIs')}{' '}
              <strong>{t('settingsPage.photoTapAndI')}</strong>{' '}
              {t('settingsPage.photoSignBody')}
            </p>
            <p className="text-sm text-muted" style={{ marginTop: 4 }}>
              <strong>{t('settingsPage.photoFixLabel')}</strong>{' '}
              {t('settingsPage.photoFixBefore')}{' '}
              <strong>{t('settingsPage.photoAppName')}</strong>
              {t('settingsPage.photoFixAfter')}
            </p>

            <hr className="divider" style={{ margin: 'var(--space-sm) 0' }} />

            <p className="text-sm">
              <strong>{t('misc.plainWay')}</strong>
            </p>
            <input
              type="file"
              onChange={uploadPhoto}
              disabled={photoState.busy}
              style={{ fontSize: 16, maxWidth: '100%', marginTop: 6 }}
            />
            <p className="hint" style={{ marginTop: 4 }}>
              {t('settingsPage.photoBrowseBefore')}{' '}
              <strong>{t('settingsPage.photoBrowseName')}</strong>
              {t('settingsPage.photoBrowseAfter')}
            </p>
          </div>
        </details>

        {/* Outside the row on purpose - see the note above it. */}
        {photoState.notice && (
          <p className="text-sm" style={{ color: 'var(--color-green)', marginTop: 'var(--space-xs)' }}>
            {photoState.notice}
          </p>
        )}
        {photoState.error && (
          <>
            <p className="field-error" style={{ marginTop: 'var(--space-xs)' }}>
              {photoState.error}
            </p>
            {photoState.detail && (
              <p className="hint" style={{ marginTop: 2 }}>
                What we received: {photoState.detail}
              </p>
            )}
          </>
        )}

        <hr className="divider" style={{ marginTop: 'var(--space-sm)' }} />

        <form className="form" onSubmit={saveProfile}>
          {profileState.error && <div className="alert alert-error">{profileState.error}</div>}
          {profileState.notice && <div className="alert alert-success">{profileState.notice}</div>}

          {/* Names, phone and school get the full width - squeezing them into
              half-width pairs made them look cramped next to the wider fields
              below, and school names in particular need the room. */}
          <div className="field">
            <label htmlFor="firstName">{t('settingsPage.firstName')}</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={profile.firstName}
              onChange={handleProfileChange}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="lastName">{t('settingsPage.lastName')}</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={profile.lastName}
              onChange={handleProfileChange}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="phone">{t('settingsPage.phone')}</label>
            <input id="phone" name="phone" type="tel" value={profile.phone} onChange={handleProfileChange} />
          </div>

          {/* Students only - people posting chores do not attend a school. */}
          {isStudent && (
            <div className="field">
              <label htmlFor="school">{t('settingsPage.school')}</label>
              <input
                id="school"
                name="school"
                type="text"
                value={profile.school}
                onChange={handleProfileChange}
              />
            </div>
          )}

          {isStudent && (
            <>
              <div className="form-row">
                {/* DATE OF BIRTH - since 17 September 2026. Shown, not edited:
                    it decides whether a parent has to agree, so a student
                    cannot change it (backend/routes/auth.js has why). The age
                    beside it is the one other people see. An account from
                    before that date, with no date yet, may add it once. */}
                {user.dateOfBirth ? (
                  <div className="field">
                    <span className="label">{t('settingsPage.dateOfBirth')}</span>
                    <span>{t('settingsPage.dateOfBirthIs', { date: birthDate(user.dateOfBirth), age: user.age })}</span>
                    <span className="hint">{t('settingsPage.dateOfBirthFixed')}</span>
                  </div>
                ) : (
                  <BirthDateFields
                    id="settings-dob"
                    value={addedBirthDate}
                    onChange={setAddedBirthDate}
                    legend={t('settingsPage.dateOfBirth')}
                    hint={t('settingsPage.dateOfBirthAdd')}
                    required={false}
                  />
                )}
                <div className="field">
                  <label htmlFor="schoolYear">{t('settingsPage.schoolYear')}</label>
                  <select id="schoolYear" name="schoolYear" value={profile.schoolYear} onChange={handleProfileChange}>
                    <option value="S5">S5</option>
                    <option value="S6">S6</option>
                    <option value="S7">S7</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="bio">{t('settingsPage.aboutYou')}</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={profile.bio}
                  onChange={handleProfileChange}
                  maxLength={500}
                  placeholder={t('settingsPage.aboutPlaceholder')}
                />
                <span className="hint">{profile.bio.length}/500</span>
              </div>

              <div className="field">
                <span className="label">{t('settingsPage.happyToDo')}</span>
                <div className="row" style={{ gap: 8 }}>
                  {CATEGORIES.map((category) => {
                    const selected = skills.includes(category);
                    return (
                      <button
                        key={category}
                        type="button"
                        className={`badge ${selected ? 'badge-green' : 'badge-grey'}`}
                        onClick={() => toggleSkill(category)}
                        aria-pressed={selected}
                        style={{ cursor: 'pointer', border: 'none' }}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="field">
                <label htmlFor="maxTravelKm">{t('settingsPage.howFar')}</label>
                <select
                  id="maxTravelKm"
                  name="maxTravelKm"
                  value={profile.maxTravelKm}
                  onChange={handleProfileChange}
                >
                  <option value="">{t('settingsPage.doNotMind')}</option>
                  <option value="2">{t('settingsPage.upToKm', { km: 2 })}</option>
                  <option value="5">{t('settingsPage.upToKm', { km: 5 })}</option>
                  <option value="10">{t('settingsPage.upToKm', { km: 10 })}</option>
                  <option value="20">{t('settingsPage.upToKm', { km: 20 })}</option>
                  <option value="50">{t('settingsPage.upToKm', { km: 50 })}</option>
                </select>
                <span className="hint">
                  {t('settingsPage.travelHint')}
                </span>
              </div>
            </>
          )}

          <hr className="divider" />
          <h4>{t('settingsPage.whereYouAre')}</h4>
          <LocationFields value={location} onChange={setLocation} required={false} />

          <button type="submit" className="btn btn-primary btn-inline" disabled={profileState.busy} style={{ alignSelf: 'flex-start' }}>
            <Save size={18} />
            {profileState.busy ? t('common.saving') : t('settingsPage.saveChanges')}
          </button>
        </form>
      </div>

      {/* ---------------- ON YOUR PHONE ---------------- */}
      {/* Everybody, both roles. Almost all of this site is used on a phone,
          and on an iPhone notifications do not work at all until it has been
          added to the home screen - which nothing in Safari tells you.
          Students get the permission button in the job alerts card below, so
          this one does not repeat it for them. */}
      <div style={{ marginBottom: 'var(--space-md)' }}>
        <InstallGuide withNotifications={!isStudent} startOpen />
      </div>

      {/* ---------------- WHAT TO BE TOLD ABOUT (both roles) ---------------- */}
      {/* A poster gets messages and applications, a student gets messages and
          job alerts - so the master switch and the message switch belong to
          both. Only the job-alert filtering below is student-shaped. */}
      <NotificationPreferences user={user} onSaved={setUser} />

      {/* ---------------- JOB ALERTS (students) ---------------- */}
      {/* Students do not read email, so this is how they hear that a job has
          been posted at all. See backend/utils/jobAlerts.js.

          Hidden when the master switch is off: every control in it would be
          one that cannot do anything, which is worse than not offering it. */}
      {isStudent && user.notifications?.enabled !== false && (
        <NotificationSettings user={user} onSaved={setUser} />
      )}

      {/* ---------------- MEMBERSHIP ---------------- */}
      {/* Hidden entirely while the site is free - there is nothing to manage. */}
      {!isStudent && subscriptionsRequired && (
        <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
          <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('settingsPage.membership')}</h3>

          <div className="row-between">
            <div className="row" style={{ gap: 'var(--space-xs)' }}>
              <CreditCard size={20} className="text-muted" aria-hidden="true" />
              <div>
                {user.subscription?.status === 'active' ? (
                  <>
                    <Badge tone="green">{t('settingsPage.active')}</Badge>
                    {user.subscription.currentPeriodEnd && (
                      <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                        Renews {dateOnly(user.subscription.currentPeriodEnd)}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Badge tone="amber">{t('settingsPage.notActive')}</Badge>
                    <p className="text-sm text-muted" style={{ marginTop: 4 }}>
                      {t('settingsPage.needMembership')}
                    </p>
                  </>
                )}
              </div>
            </div>

            <Link to="/subscribe" className="btn btn-outline btn-sm btn-inline">
              {user.subscription?.status === 'active' ? t('settingsPage.manage') : t('settingsPage.join')}
            </Link>
          </div>
        </div>
      )}

      {/* ---------------- TWO-STEP LOGIN (moderators) ---------------- */}
      {/* Renders nothing unless the server says this account is a moderator -
          it asks rather than reading a flag off the user object, so a stale
          copy of the account cannot put the card on the wrong screen. */}
      <TwoFactorSettings />

      {/* ---------------- PASSWORD ---------------- */}
      <div className="card">
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('settingsPage.changePassword')}</h3>

        <form className="form" onSubmit={savePassword}>
          {passwordState.error && <div className="alert alert-error">{passwordState.error}</div>}
          {passwordState.notice && <div className="alert alert-success">{passwordState.notice}</div>}

          <div className="form-row">
            <div className="field">
              <label htmlFor="currentPassword">{t('settingsPage.currentPassword')}</label>
              <PasswordInput
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
                value={passwords.currentPassword}
                onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="newPassword">{t('settingsPage.newPassword')}</label>
              <PasswordInput
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                value={passwords.newPassword}
                onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}
                required
              />
              <span className="hint">{t('settingsPage.atLeast8')}</span>
            </div>
          </div>

          <button type="submit" className="btn btn-secondary btn-inline" disabled={passwordState.busy} style={{ alignSelf: 'flex-start' }}>
            <KeyRound size={18} />
            {passwordState.busy ? t('common.saving') : t('settingsPage.updatePassword')}
          </button>
        </form>
      </div>

      {/* ---------------- BLOCKED PEOPLE ---------------- */}
      <div className="card" style={{ marginTop: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-xs)' }}>{t('settingsPage.blocked')}</h3>
        {blocks.length === 0 ? (
          <p className="text-muted text-sm">
            {t('settingsPage.noBlocks')}
          </p>
        ) : (
          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {blocks.map((b) => (
              <div className="row-between" key={b._id}>
                <span>
                  {b.blocked?.firstName} {b.blocked?.lastName}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-inline"
                  onClick={() => unblock(b.blocked._id)}
                >
                  {t('settingsPage.unblock')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---------------- CLOSE ACCOUNT ---------------- */}
      <div className="card" style={{ marginTop: 'var(--space-md)', borderColor: 'var(--color-error)' }}>
        <h3 style={{ marginBottom: 'var(--space-xs)' }}>{t('settingsPage.closeAccount')}</h3>
        <p className="text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
          {t('settingsPage.closeAccountBody')}
        </p>

        <button
          type="button"
          className="btn btn-outline btn-inline"
          onClick={() => setDeleteOpen(true)}
          style={{ alignSelf: 'flex-start', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
        >
          <Trash2 size={18} /> {t('settingsPage.closeMyAccount')}
        </button>
      </div>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('settingsPage.closeAccountQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setDeleteOpen(false)}>
              {t('settingsPage.keepMyAccount')}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-inline"
              onClick={deleteAccount}
              disabled={deleteState.busy || !deletePassword}
            >
              {deleteState.busy ? t('settingsPage.closing') : t('settingsPage.closeForever')}
            </button>
          </>
        }
      >
        <div className="alert alert-warning" style={{ marginBottom: 'var(--space-sm)' }}>
          <AlertTriangle size={18} aria-hidden="true" />
          <div className="text-sm">{t('settingsPage.cannotUndo')}</div>
        </div>

        <p style={{ marginBottom: 'var(--space-xs)' }}>
          <strong>{t('settingsPage.whatGoes')}</strong> your name, email, phone, location
          {isStudent ? ', bio, photo, bank details and your parent or guardian&apos;s details' : ''}.
        </p>
        <p className="text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
          <strong>{t('settingsPage.whatStays')}</strong> {t('settingsPage.whatStaysBody')}
        </p>
        <p className="text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
          {t('settingsPage.openWillBeCancelled')}
        </p>

        {deleteState.error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>
            {deleteState.error}
          </div>
        )}

        <div className="field">
          <label htmlFor="delete-password">{t('settingsPage.confirmPassword')}</label>
          <PasswordInput
            id="delete-password"
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            autoComplete="current-password"
          />
        </div>
      </Modal>
    </div>
  );
}
